import { motion } from 'framer-motion'
import { Phone, Mail, Clock, MapPin } from 'lucide-react'

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
}

export default function ApoioAPAV() {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen bg-white"
        >
            <title>Apoio a Vítimas LGBTI+: Serviços e Contactos | APAV</title>
            <meta name="description" content="A APAV apoia vítimas de qualquer crime ou violência. Conheça os serviços de apoio a pessoas LGBTI+: GAV de Oeiras e APAV SAFE. Moradas, horários e contactos." />
            <link rel="canonical" href="https://lgbti.apav.pt/ApoioAPAV" />
            {/* Hero Section */}
            <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-brand-900 to-purple-900 py-20 lg:py-28">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-96 h-96 bg-brand-400 rounded-full filter blur-3xl -translate-x-1/2 -translate-y-1/2" />
                    <div className="absolute bottom-0 right-0 w-80 h-80 bg-purple-400 rounded-full filter blur-3xl translate-x-1/2 translate-y-1/2" />
                </div>
                <div className="container-custom relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="grid md:grid-cols-2 gap-12 items-center"
                    >
                        <div>
                            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                O Apoio da APAV
                            </h1>
                            <p className="text-lg text-slate-300 leading-relaxed">
                                A APAV presta apoio a todas as pessoas que tenham sido vítimas de qualquer forma de crime ou violência, motivados ou não pela sua identidade de género, características e/ou orientação sexual.
                            </p>
                        </div>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.7, delay: 0.3 }}
                            className="relative"
                        >
                            <div className="absolute inset-0 rounded-2xl bg-brand-500/20 blur-xl transform scale-105" />
                            <img
                                src="/assets/images/logo-116006-08h-23h-2025.svg"
                                alt="Linha de Apoio à Vítima 116 006"
                                className="w-full rounded-2xl shadow-2xl relative z-10 bg-white p-6"
                            />
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* GAV Oeiras */}
            <section className="py-10 lg:py-14 bg-white">
                <div className="container-custom">
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="grid md:grid-cols-2 gap-12 items-center"
                    >
                        <div className="order-2 md:order-1">
                            <span className="inline-block px-3 py-1 rounded-full bg-brand-100 text-brand-700 text-xs font-bold mb-4 uppercase tracking-wider">
                                Serviço de Proximidade
                            </span>
                            <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-6">
                                Gabinete de Apoio à Vítima de Oeiras | LGBTI+
                            </h2>
                            <div className="space-y-4">
                                <div className="flex items-start gap-3">
                                    <div className="w-9 h-9 bg-brand-50 rounded-xl flex items-center justify-center shrink-0 border border-brand-100">
                                        <MapPin size={17} className="text-brand-600" />
                                    </div>
                                    <div className="text-slate-700 text-base leading-relaxed">
                                        <p className="font-semibold text-slate-900">Esquadra de Oeiras da PSP</p>
                                        <p>Rua do Espargal, 18</p>
                                        <p>2780-012 OEIRAS</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 bg-brand-50 rounded-xl flex items-center justify-center shrink-0 border border-brand-100">
                                        <Clock size={17} className="text-brand-600" />
                                    </div>
                                    <p className="text-slate-700">10H00 - 18H00 (dias úteis)</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 bg-brand-50 rounded-xl flex items-center justify-center shrink-0 border border-brand-100">
                                        <Phone size={17} className="text-brand-600" />
                                    </div>
                                    <a href="tel:211379446" className="text-slate-900 font-bold hover:underline focus-visible:ring-2 focus-visible:ring-brand-600 rounded-sm">211 379 446</a>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 bg-brand-50 rounded-xl flex items-center justify-center shrink-0 border border-brand-100">
                                        <Mail size={17} className="text-brand-600" />
                                    </div>
                                    <a href="mailto:apav.oeiras@apav.pt" className="text-brand-700 font-bold hover:underline">
                                        apav.oeiras@apav.pt
                                    </a>
                                </div>
                            </div>
                        </div>
                        <div className="order-1 md:order-2">
                            <img
                                src="/assets/images/monofolha-lgbt-oeiras-1.webp"
                                alt="GAV Oeiras LGBTI+"
                                className="w-full rounded-2xl shadow-lg"
                            />
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* APAV SAFE */}
            <section className="py-10 lg:py-14 bg-slate-50">
                <div className="container-custom">
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="grid md:grid-cols-2 gap-12 items-center"
                    >
                        <div>
                            <div className="bg-white rounded-2xl p-8 shadow-md border border-slate-100 flex items-center justify-center h-56">
                                <img
                                    src="/assets/images/logo-apavsafe.png"
                                    alt="APAV SAFE"
                                    className="max-h-full max-w-full object-contain"
                                />
                            </div>
                        </div>
                        <div>
                            <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-6">
                                APAV SAFE
                            </h2>
                            <div className="space-y-4">
                                <div className="flex items-start gap-3">
                                    <div className="w-9 h-9 bg-brand-50 rounded-xl flex items-center justify-center shrink-0 border border-brand-100">
                                        <MapPin size={17} className="text-brand-600" />
                                    </div>
                                    <div className="text-slate-700 text-base leading-relaxed">
                                        <p className="font-semibold text-slate-900">Serviços de Apoio à Vítima de Lisboa</p>
                                        <p>Rua José Estêvão, 135 A, Piso 1</p>
                                        <p>1150-201 LISBOA</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 bg-brand-50 rounded-xl flex items-center justify-center shrink-0 border border-brand-100">
                                        <Clock size={17} className="text-brand-600" />
                                    </div>
                                    <p className="text-slate-700">10H00 – 13H00 / 14H00 – 17H30 (dias úteis)</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 bg-brand-50 rounded-xl flex items-center justify-center shrink-0 border border-brand-100">
                                        <Phone size={17} className="text-brand-600" />
                                    </div>
                                    <a href="tel:213587914" className="text-slate-900 font-bold hover:underline focus-visible:ring-2 focus-visible:ring-brand-600 rounded-sm">21 358 79 14</a>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 bg-brand-50 rounded-xl flex items-center justify-center shrink-0 border border-brand-100">
                                        <Mail size={17} className="text-brand-600" />
                                    </div>
                                    <a href="mailto:safe@apav.pt" className="text-brand-700 font-bold hover:underline">
                                        safe@apav.pt
                                    </a>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>
        </motion.div>
    )
}
